import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const GOLD = '#B8860B';

export default function VerifyEmail() {
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isWaitlist, setIsWaitlist] = useState(false);
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Check if this user is a waitlist user to show correct messaging
  useEffect(() => {
    const checkUserType = async () => {
      if (user) {
        const waitlistRef = doc(db, 'waitlist', user.uid);
        const snap = await getDoc(waitlistRef);
        if (snap.exists()) setIsWaitlist(true);
      }
    };
    checkUserType();
  }, [user]);
  
  // Handle countdown for resend button
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await sendEmailVerification(user);
      toast.success("Verification link dispatched.");
      setCooldown(60);
    } catch (error) {
      toast.error("Rate limit hit.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div style={containerStyle}>
      <div style={glowStyle} />

      <div style={cardStyle}>
        <div style={iconContainer}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>

        <h1 style={titleStyle}>{isWaitlist ? "WAITLIST VERIFICATION" : "SECURE YOUR VAULT"}</h1>
        <p style={subtitleStyle}>
          {isWaitlist 
            ? "A waitlist confirmation link has been sent to:" 
            : "A security verification link has been sent to:"}
          <br />
          <span style={{ color: GOLD, fontWeight: 'bold' }}>{user?.email}</span>
        </p>

        <div style={statusBox}>
          <p style={statusText}>
            {isWaitlist 
              ? "Verify your email to secure your spot in the queue. After verification, we will review your profile for approval."
              : "Please click the link in your email to activate your Esdras account. This will grant you access to your 3D dashboard."}
          </p>
        </div>

        <div style={actionArea}>
          <button onClick={() => window.location.reload()} style={primaryBtn}>
            I'VE CLICKED THE LINK
          </button>

          <button onClick={handleResend} disabled={loading || cooldown > 0} style={secondaryBtn}>
            {cooldown > 0 ? `RESEND IN ${cooldown}s` : 'RESEND LINK'}
          </button>
        </div>

        <button onClick={handleLogout} style={logoutBtn}>
          BACK TO LOGIN
        </button>
      </div>

      <p style={footerNote}>ESDRAS ROOT • SECURITY PROTOCOL 4.0</p>
    </div>
  );
} 

// --- STYLES ---

const containerStyle = {
  minHeight: '100vh',
  background: '#000',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'relative',
  overflow: 'hidden',
  fontFamily: 'Inter, sans-serif'
};

const glowStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '400px',
  height: '400px',
  background: GOLD,
  opacity: 0.05,
  filter: 'blur(100px)',
  borderRadius: '50%',
  pointerEvents: 'none'
};

const cardStyle = {
  width: '100%',
  maxWidth: '400px',
  textAlign: 'center',
  zIndex: 1
};

const iconContainer = {
  marginBottom: '30px',
  animation: 'float 3s ease-in-out infinite'
};

const titleStyle = {
  color: '#FFF',
  fontSize: '1.5rem',
  letterSpacing: '4px',
  margin: '0 0 10px 0',
  fontWeight: '900'
};

const subtitleStyle = {
  color: '#888',
  fontSize: '0.85rem',
  lineHeight: '1.6',
  marginBottom: '30px'
};

const statusBox = {
  background: '#0a0a0a',
  border: '1px solid #1a1a1a',
  padding: '20px',
  borderRadius: '12px',
  marginBottom: '30px'
};

const statusText = {
  color: '#666',
  fontSize: '0.75rem',
  lineHeight: '1.8',
  margin: 0
};

const actionArea = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
};

const primaryBtn = {
  background: GOLD,
  color: '#000',
  border: 'none',
  padding: '16px',
  borderRadius: '8px',
  fontWeight: '900',
  fontSize: '0.75rem',
  letterSpacing: '1px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease'
};

const secondaryBtn = {
  background: 'transparent',
  border: '1px solid #333',
  color: '#FFF',
  padding: '14px',
  borderRadius: '8px',
  fontSize: '0.7rem',
  letterSpacing: '1px',
  cursor: 'pointer'
};

const logoutBtn = {
  marginTop: '30px',
  background: 'transparent',
  border: 'none',
  color: '#444',
  fontSize: '0.65rem',
  letterSpacing: '2px',
  cursor: 'pointer',
  textDecoration: 'underline'
};

const footerNote = {
  position: 'absolute',
  bottom: '30px',
  color: '#222',
  fontSize: '0.6rem',
  letterSpacing: '3px'
};
